<template>
	<blue-confirm-dialog ref="dialog">
		<template #text>
			<div class="d-flex justify-space-between">
				<div>
					<span>Code:</span>
					<h3>{{ billable.code }}</h3>
				</div>
				<div>
					<span>Currency:</span>
					<div class="d-flex align-center">
						<v-avatar color="indigo" size="28" class="mr-1">
							<v-img
								v-if="billable.currency"
								:src="require(`../../assets/currencies/${billable.currency.toLowerCase()}.png`)"
							/>
						</v-avatar>
						<h3>{{billable.currency}}</h3>
					</div>
				</div>
			</div>
			<div class="my-5 d-flex flex-column" style="gap: 20px">
				<v-divider></v-divider>
				<div class="d-flex flex-column" style="gap: 5px">
					<b>Contracted Hours vs. Billable Executed Hours</b>
					<billable-percentage-bar
						:primary-amount="billable.executedBillableHours"
						:objective="billable.contractedHours"
						:amount-formatter="m => m.toFixed(0) + ' hrs'"
					/>
				</div>
				<v-divider></v-divider>
				<div class="d-flex flex-column" style="gap: 5px">
					<b>Billable Executed Hours vs. Billable items</b>
					<billable-percentage-bar
						:primary-amount="billable.billedHours"
						:objective="billable.executedBillableHours"
						:amount-formatter="m => m.toFixed(0) + ' hrs'"
						overflow-error
					/>
				</div>
				<v-divider></v-divider>
				<div class="d-flex flex-column" style="gap: 5px">
					<b>Billable Items vs. Invoices</b>
					<billable-percentage-bar
						:primary-amount="billable.invoiced"
						:objective="billable.billed"
						:amount-formatter="currencyFormat"
					/>
				</div>
				<v-divider></v-divider>
			</div>

			<template v-if="!isReopen && hasBlockingItems">
				<v-alert type="warning" dense outlined class="mb-4">
					There are related documents that need attention before closing this billable.
				</v-alert>

				<div v-if="blockingBillableItems.length" class="mb-4">
					<h4 class="mb-2">Billable Items</h4>
					<v-data-table
						:headers="billableItemHeaders"
						:items="blockingBillableItems"
						dense hide-default-footer
						disable-sort
						class="rounded-lg bordered new-back lighten2 overflow-hidden"
					>
						<template #[`item.statusDisplayName`]="{ item }">
							<v-chip
								x-small
								:color="item.statusLightColor"
								:style="{ color: item.statusColor }"
							>{{ item.statusDisplayName }}</v-chip>
						</template>
						<template #[`item.total`]="{ item }">
							{{ currencyFormat(item.total) }}
						</template>
						<template #[`item.invoiced`]="{ item }">
							{{ currencyFormat(item.invoiced) }}
						</template>
					</v-data-table>
				</div>

				<div v-if="blockingInvoices.length">
					<h4 class="mb-2">Invoices</h4>
					<v-data-table
						:headers="invoiceHeaders"
						:items="blockingInvoices"
						dense hide-default-footer
						disable-sort
						class="rounded-lg bordered new-back lighten2 overflow-hidden"
					>
						<template #[`item.statusDisplayName`]="{ item }">
							<v-chip
								x-small
								:color="item.statusLightColor"
								:style="{ color: item.statusColor }"
							>{{ item.statusDisplayName }}</v-chip>
						</template>
						<template #[`item.subtotal`]="{ item }">
							{{ currencyFormat(item.subtotal) }}
						</template>
						<template #[`item.billed`]="{ item }">
							{{ currencyFormat(item.billed) }}
						</template>
					</v-data-table>
				</div>
			</template>

			<div v-if="!hasBlockingItems || isReopen" class="mb-n5 body-1 text-right">
				Are you sure you want to {{ isReopen ? 'reopen' : 'close' }} this billable?
			</div>
		</template>

		<template #actions="{ cancel, confirm }">
			<v-spacer/>
			<v-btn @click="cancel()" text>Cancel</v-btn>
			<v-btn
				@click="confirm()"
				color="primary" text
				:disabled="!isReopen && hasBlockingItems"
			>Confirm</v-btn>
		</template>
	</blue-confirm-dialog>
</template>

<script>
import BillablePercentageBar from '../../components/admin-surf/billable/BillablePercentageBar'

export default {
	components: {
		BillablePercentageBar,
	},
	data: () => ({
		billable: {},
		resolve: null,
		isReopen: false,
		blockingBillableItems: [],
		blockingInvoices: [],
		billableItemHeaders: [
			{ text: 'Code', value: 'code' },
			{ text: 'Status', value: 'statusDisplayName' },
			{ text: 'Subtotal', value: 'total', align: 'right' },
			{ text: 'Invoiced', value: 'invoiced', align: 'right' },
		],
		invoiceHeaders: [
			{ text: 'Invoice No.', value: 'code' },
			{ text: 'Status', value: 'statusDisplayName' },
			{ text: 'Subtotal', value: 'subtotal', align: 'right' },
			{ text: 'Billed', value: 'billed', align: 'right' },
		],
	}),
	computed: {
		hasBlockingItems() {
			return this.blockingBillableItems.length > 0 || this.blockingInvoices.length > 0
		},
	},
	methods: {
		async open(id, isReopen = false){
			this.isReopen = isReopen
			this.blockingBillableItems = []
			this.blockingInvoices = []

			try {
				const [billableRes, precheckRes] = await Promise.all([
					this.$http.get(`api/billable/${id}`),
					isReopen ? Promise.resolve({ data: { billableItems: [], invoices: [] } }) : this.$http.get(`api/billable/${id}/closePrecheck`),
				])
				this.billable = billableRes.data
				this.blockingBillableItems = precheckRes.data.billableItems || []
				this.blockingInvoices = precheckRes.data.invoices || []
			} catch(e) {
				console.error(e)
				this.$root.error('Failed to load billable')
			}

			const dialogWidth = this.hasBlockingItems && !isReopen ? 700 : 500
			const confirmed = await this.$refs.dialog.open({
				title: isReopen ? 'Reopen Billable' : 'Close Billable',
				width: dialogWidth,
			})
			if(!confirmed) return

			try {
				const billableRes = await this.$http.post(`api/billable/${id}/${ isReopen ? 'reopen' : 'close' }`)
				return billableRes.data
			} catch(e) {
				console.error(e)
				this.$root.error('Failed to close billable')
			}
		},
	},
}
</script>

<style lang="scss" scoped>

</style>
